// MyFreezers.jsx — MUI DataGrid refactor (JS only, no TypeScript syntax)
import * as React from "react";
import styles from "./freezers.module.scss";
import {
  GET_COMPANY_ALERT_PARAMS,
  GET_COMPANY_OPES,
  GET_FREE_DEVICES,
  GET_REFRIGERATIONS,
  UPDATE_REFRIGERATION,
} from "../../../../queries";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridRowModes,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import CreateFreezerModal from "./createFreezerModal";
import { language } from "../../../../const";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";

const ResponsibleEditCell = (props) => {
  const { id, value, api, field } = props;
  const selected = Array.isArray(value) ? value : [];

  const handleChange = (event) => {
    const val = event.target.value || [];
    api.setEditCellValue({ id, field, value: val }, event);
  };

  // Options passed via colDef.valueOptions
  const options = (props.colDef && props.colDef.valueOptions) || [];

  return (
    <Select
      multiple
      value={selected}
      onChange={handleChange}
      size="small"
      fullWidth
      renderValue={(sel) =>
        (!sel || sel.length === 0)
          ? (language === "es" ? "Seleccionar…" : "Select…")
          : sel
              .map((opId) => {
                const opt = options.find((o) => o.id === opId);
                return opt ? opt.label : String(opId).split("-")[1] || opId;
              })
              .join(", ")
      }
      MenuProps={{
        PaperProps: { style: { maxHeight: 420 } },
      }}
    >
      {options.map((opt) => (
        <MenuItem key={opt.id} value={opt.id}>
          <Checkbox checked={selected.includes(opt.id)} />
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  );
};

const TempRangeEditCell = (props) => {
  const { id, value, api, field } = props;
  const handleChange = (e) => {
    api.setEditCellValue({ id, field, value: e.target.value }, e);
  };
  const options = (props.colDef && props.colDef.valueOptions) || [];
  const current = value == null ? "" : value;
  const list = [current, ...options.filter((o) => o !== current)];
  return (
    <Select value={current} onChange={handleChange} size="small" fullWidth displayEmpty>
      {list.map((opt, idx) => (
        <MenuItem key={(opt || "none") + idx} value={opt || ""}>
          {opt || (language === "es" ? "Sin rango" : "No range")}
        </MenuItem>
      ))}
    </Select>
  );
};

const StatusEditCell = (props) => {
  const { id, value, api, field } = props;
  const handleChange = (e) => {
    api.setEditCellValue({ id, field, value: e.target.value }, e);
  };
  const options = (props.colDef && props.colDef.valueOptions) || [];
  const current = value || "ACTIVE";
  return (
    <Select value={current} onChange={handleChange} size="small" fullWidth>
      {options.map((o) => (
        <MenuItem key={o.id} value={o.id}>
          {o.label}
        </MenuItem>
      ))}
    </Select>
  );
};

const MyFreezers = ({ company_id }) => {
  const [freezerModal, setFreezerModal] = React.useState(false);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const intl = useIntl();

  const [getRefrigerations, { data: refrigerations, loading: refrigerationsLoading }] =
    useLazyQuery(GET_REFRIGERATIONS, {
      variables: { assigned_to: company_id || "" },
      fetchPolicy: "network-only",
    });

  const [getCompanyAlerts, { data: companyDetailData }] = useLazyQuery(GET_COMPANY_ALERT_PARAMS);
  const [getCompanyOpes, { data: companyOpes }] = useLazyQuery(GET_COMPANY_OPES);
  const [getFreeDevices, { data: freeDevices }] = useLazyQuery(GET_FREE_DEVICES);

  const [updateRefrigeration, { loading: updateLoading }] = useMutation(UPDATE_REFRIGERATION, {
    refetchQueries: [{ query: GET_REFRIGERATIONS, variables: { assigned_to: company_id || "" } }],
  });

  React.useEffect(() => {
    (async () => {
      await getCompanyAlerts({ variables: { compani_id: company_id } });
      await getFreeDevices({ variables: { company_id, device_format: "RC_Static" } });
      await getCompanyOpes({ variables: { compani_id: company_id } });
      await getRefrigerations();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company_id]);

  const rows = React.useMemo(() => {
    const list = (refrigerations && refrigerations.refrigerations) || [];
    return list.map((r) => ({
      id: r.refrigeration_id,
      refrigeration_id: r.refrigeration_id,
      name: r.name,
      type: r.type,
      qr: r.qr,
      temp_range: (r.temperature_range && r.temperature_range.name) || null,
      status: r.status,
      responsible: r.responsible || [],
    }));
  }, [refrigerations]);

  const tempRangeOptions = React.useMemo(() => {
    const arr = (companyDetailData && companyDetailData.company && companyDetailData.company.alert_params && companyDetailData.company.alert_params.temperature_alerts) || [];
    return arr.map((t) => t && t.name).filter(Boolean);
  }, [companyDetailData]);

  const operatorsAllMap = React.useMemo(() => {
    const ops = (companyOpes && companyOpes.company && companyOpes.company.operators) || [];
    const map = {};
    ops.forEach((o) => { if (o && o.operator_id) map[o.operator_id] = o.name; });
    return map; // incluye activos e inactivos
  }, [companyOpes]);

  const operatorsActiveOptions = React.useMemo(() => {
    const ops = (companyOpes && companyOpes.company && companyOpes.company.operators) || [];
    return ops
      .filter((o) => o.status === "ACTIVE")
      .map((o) => ({ id: o.operator_id, label: o.name }));
  }, [companyOpes]);

  const statusOptions = React.useMemo(() => [
    { id: "ACTIVE", label: intl.formatMessage({ id: "active" }) },
    { id: "INACTIVE", label: intl.formatMessage({ id: "inactive" }) },
  ], [intl]);

  const handleEditClick = (id) => () => setRowModesModel((m) => ({ ...m, [id]: { mode: GridRowModes.Edit } }));
  const handleSaveClick = (id) => () => setRowModesModel((m) => ({ ...m, [id]: { mode: GridRowModes.View } }));
  const handleCancelClick = (id) => () => setRowModesModel((m) => ({ ...m, [id]: { mode: GridRowModes.View, ignoreModifications: true } }));

  const processRowUpdate = async (newRow, oldRow) => {
    const ok = window.confirm(intl.formatMessage({ id: "are_you_sure_confirmChanges" }));
    if (!ok) return oldRow;

    const responsible2 = Array.isArray(newRow.responsible) && newRow.responsible.length > 0 ? newRow.responsible : null;

    try {
      await updateRefrigeration({
        variables: {
          name: newRow.name || null,
          temperature_range: newRow.temp_range || null,
          refrigeration_id: newRow.refrigeration_id,
          type: newRow.type || null,
          status: newRow.status || null,
          responsible: responsible2,
        },
      });
      toast.success(language === "es" ? "Guardado correctamente" : "Saved successfully");
      return newRow;
    } catch (err) {
      console.error(err);
      toast.error(intl.formatMessage({ id: "device_using_temp" }));
      throw err;
    }
  };

  const t = (es, en) => (language === "es" ? es : en);

  const columns = React.useMemo(() => [
    {
      field: "name",
      headerName: t("NOMBRE", "NAME"),
      flex: 1.1,
      minWidth: 150,
      editable: true,
    },
    {
      field: "type",
      headerName: t("TIPO", "TYPE"),
      flex: 1,
      minWidth: 140,
      editable: true,
    },
    {
      field: "qr",
      headerName: t("ASIGNADO AL QR", "ASSIGNED TO QR"),
      flex: 1,
      minWidth: 160,
      sortable: true,
      filterable: true,
      editable: false,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "temp_range",
      headerName: t("RANGO TEMP.", "TEMP. RANGE"),
      flex: 0.9,
      minWidth: 140,
      editable: true,
      type: "singleSelect",
      valueOptions: tempRangeOptions,
      renderEditCell: (p) => (
        <TempRangeEditCell {...p} colDef={{ ...p.colDef, valueOptions: tempRangeOptions }} />
      ),
    },
    {
      field: "status",
      headerName: t("ESTADO", "STATUS"),
      flex: 0.9,
      minWidth: 130,
      editable: true,
      type: "singleSelect",
      valueOptions: statusOptions.map((s) => s.id),
      renderCell: (p) => (p.value === "INACTIVE" ? intl.formatMessage({ id: "inactive" }) : intl.formatMessage({ id: "active" })),
      renderEditCell: (p) => (
        <StatusEditCell {...p} colDef={{ ...p.colDef, valueOptions: statusOptions }} />
      ),
    },
    {
      field: "responsible",
      headerName: t("RESPONSABLE", "RESPONSIBLE"),
      flex: 1.2,
      minWidth: 220,
      editable: true,
      sortable: false,
      filterable: false,
      renderCell: (p) => {
        const ids = Array.isArray(p.value) ? p.value : [];
        if (!ids.length) return <Typography variant="body2">--</Typography>;
        const labels = ids.map((id) => operatorsAllMap[id]).filter(Boolean);
        const fallback = ids.map((id) => String(id).split("-")[1] || id);
        const toShow = labels.length ? labels : fallback;
        return <Typography variant="body2">{toShow.join(", ")}</Typography>;
      },
      renderEditCell: (p) => (
        <ResponsibleEditCell {...p} colDef={{ ...p.colDef, valueOptions: operatorsActiveOptions }} />
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "",
      width: 110,
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id] && rowModesModel[id].mode === GridRowModes.Edit;
        if (isInEditMode) {
          return [
            <GridActionsCellItem key="save" icon={<SaveIcon />} label={t("Guardar", "Save")} onClick={() => handleSaveClick(id)()} />,
            <GridActionsCellItem key="cancel" icon={<CloseIcon />} label={t("Cancelar", "Cancel")} onClick={() => handleCancelClick(id)()} color="inherit" />,
          ];
        }
        return [
          <GridActionsCellItem key="edit" icon={<EditIcon />} label={t("Editar", "Edit")} onClick={() => handleEditClick(id)()} color="inherit" />,
        ];
      },
    },
  ], [intl, rowModesModel, operatorsAllMap, operatorsActiveOptions, statusOptions, tempRangeOptions]);

  const empty = !refrigerations || !refrigerations.refrigerations || refrigerations.refrigerations.length === 0;

  return (
    <Box className={styles.freezersContainer}>
      <ToastContainer />

      {(refrigerationsLoading || updateLoading) && (
        <Box className={styles.loading} sx={{ py: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {!empty ? (
        <Box sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(r) => r.id}
            density="compact"
            disableRowSelectionOnClick
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={() => {}}
            pagination
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } },
              columns: { columnVisibilityModel: { qr: true } },
              sorting: { sortModel: [{ field: "name", sort: "asc" }] },
            }}
            sx={{
              "& .MuiDataGrid-columnHeaders": { color: "#00ABC8", fontWeight: 600 },
              "& .MuiDataGrid-cell": { color: "#1B1464" },
              "& .MuiDataGrid-row:nth-of-type(odd)": { backgroundColor: "#FAFAFA" },
              "& .MuiDataGrid-row:nth-of-type(even)": { backgroundColor: "#D9F1F5" },
              border: 0,
            }}
            localeText={{ noRowsLabel: language === "es" ? "Sin registros" : "No rows" }}
          />
        </Box>
      ) : null}

      <Box sx={{ display: "flex", gap: 2, mt: 2, justifyContent: "flex-start" }}>
        <Button onClick={() => setFreezerModal(true)} className={styles.createFreezerButton} variant="outlined">
          {localStorage.getItem("language") === "es" ? "Nueva heladera" : "New Refrigerator"}
        </Button>
      </Box>

      <CreateFreezerModal
        freezerModal={freezerModal}
        closeCreateFreezerModal={() => setFreezerModal(false)}
        companyDetailData={companyDetailData}
        freeDevices={freeDevices && freeDevices.freeDevices}
      />
    </Box>
  );
};

export default MyFreezers;
